/**
 * AdminApp - 수집 관리 콘솔 셸
 *
 * 책임:
 * - AuthProvider + ToastProvider + BrowserRouter 조립 (전역 상태 + 라우팅)
 * - useAuth() 로그인 상태로 LoginScreen 또는 앱 렌더링
 * - URL 라우팅 (각 화면이 고유 URL) — 서브패스는 BrowserRouter basename으로 흡수
 * - 각 화면의 Container 컴포넌트 렌더링
 *
 * 구조:
 * AdminApp (Providers + Router 조립)
 *   → AppContent (useAuth로 로그인 상태 확인)
 *       → LoginContainer (로그인하지 않음)
 *       또는
 *       → SideNav + AppHeader + <Routes> (로그인함)
 *
 * URL 구조 (basename=PUBLIC_URL 기준):
 *   /dashboard · /modules(?focus=) · /jobs(?module=) · /jobs/:id · /data-browser · /population
 *   그 외/루트 → /dashboard 로 리다이렉트
 */

/** @jsxImportSource @emotion/react */
import { useEffect, useState } from 'react';
import { css } from '@emotion/react';
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useNavigate,
  useLocation,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import { useAuth } from './hooks/useAuth';
import * as endpoints from './api/endpoints';
import { AuthProvider } from './context/AuthContext';
import { ToastProvider, ToastContainer } from './context/ToastContext';
import { SideNav } from './organisms/SideNav/SideNav';
import { AppHeader } from './organisms/AppHeader/AppHeader';
import { LoginContainer } from './containers/LoginContainer';
import { DashboardContainer } from './containers/DashboardContainer';
import { ModulesContainer } from './containers/ModulesContainer';
import { JobsContainer } from './containers/JobsContainer';
import { JobDetailContainer } from './containers/JobDetailContainer';
import { DataBrowserContainer } from './containers/DataBrowserContainer';
import { PopulationContainer } from './containers/PopulationContainer';
import { theme } from '../design-system/theme/theme';

// 사이드 네비게이션 항목. id가 곧 경로 세그먼트(`/${id}`)와 1:1 대응한다.
const NAV_ITEMS = [
  { id: 'dashboard', label: '대시보드' },
  { id: 'modules', label: '모듈 관리' },
  { id: 'jobs', label: '수집 이력' },
  { id: 'data-browser', label: '데이터 브라우저' },
  { id: 'population', label: '집계 뷰어' },
];

const shellStyle = css`
  display: flex;
  min-height: 100vh;
  background: ${theme.color.bg};
  color: ${theme.color.text};
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Noto Sans KR', sans-serif;
`;

const mainStyle = css`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
`;

const contentStyle = css`
  flex: 1;
  overflow: auto;
  padding: ${theme.space.lg}px;
`;

// ── 라우트 글루 ─────────────────────────────────────────
// URL(params/searchParams) ↔ 기존 Container props 를 잇는 얇은 래퍼.
// Container 들의 인터페이스는 그대로 두고, 네비게이션만 navigate()로 바꾼다.

/** 대시보드 — 히트맵/편성표/헬스에서의 이동을 URL 이동으로 연결. */
function DashboardRoute() {
  const navigate = useNavigate();
  return (
    <DashboardContainer
      onOpenJob={(id) => navigate(`/jobs/${id}`)}
      onOpenHistory={(m) => navigate(`/jobs?module=${encodeURIComponent(m)}`)}
      onOpenModule={(m) => navigate(`/modules?focus=${encodeURIComponent(m)}`)}
    />
  );
}

/** 모듈 관리 — ?focus=<모듈>로 진입 시 해당 모듈로 스크롤, 처리 후 쿼리 제거. */
function ModulesRoute() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const focus = params.get('focus');
  return (
    <ModulesContainer
      onOpenJob={(id) => navigate(`/jobs/${id}`)}
      focusModule={focus}
      onFocusHandled={() => {
        // 스크롤은 한 번만 → focus 쿼리를 지운다. 뒤로가기 히스토리 오염 방지로 replace.
        const next = new URLSearchParams(params);
        next.delete('focus');
        setParams(next, { replace: true });
      }}
    />
  );
}

/** 수집 이력 — ?module=<모듈>을 초기 필터로 적용(딥링크 재현). */
function JobsRoute() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const module = params.get('module') ?? undefined;
  // module 쿼리를 key로 → 값이 바뀌면 리마운트되어 URL이 초기 필터의 기준이 된다.
  // (같은 /jobs 경로라도 ?module 변화·제거가 필터에 반영됨)
  return (
    <JobsContainer
      key={module ?? ''}
      onOpenJob={(id) => navigate(`/jobs/${id}`)}
      initialModule={module}
    />
  );
}

/** Job 상세 — /jobs/:id. 뒤로가기는 목록으로, 관련 Job 링크는 해당 상세로. */
function JobDetailRoute() {
  const navigate = useNavigate();
  const { id } = useParams();
  const jobId = id !== undefined ? Number(id) : NaN;
  return (
    <JobDetailContainer
      jobId={Number.isFinite(jobId) ? jobId : null}
      onBack={() => navigate('/jobs')}
      onOpenJob={(jid) => navigate(`/jobs/${jid}`)}
    />
  );
}

/**
 * AppContent: 로그인 상태에 따라 화면 렌더링
 * - useAuth()로 로그인 상태 확인
 * - SideNav/헤더는 현재 경로에서 파생, 콘텐츠는 <Routes>로 분기
 */
function AppContent() {
  const { isLoggedIn, user, logout, token } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // 실행 중(RUNNING) Job이 하나라도 있으면 스케줄러 가동 중으로 간주.
  // KPI만 가볍게 폴링(30초)해 사이드바 상태 표시에 사용.
  const [schedulerRunning, setSchedulerRunning] = useState(false);
  useEffect(() => {
    if (!token) return;
    let alive = true;
    const check = () => {
      endpoints
        .fetchDashboardKpi()
        .then((kpi) => alive && setSchedulerRunning(kpi.running > 0))
        .catch(() => alive && setSchedulerRunning(false));
    };
    check();
    const timer = setInterval(check, 30000);
    return () => {
      alive = false;
      clearInterval(timer);
    };
  }, [token]);

  // 로그인하지 않음 → LoginContainer 렌더링
  if (!isLoggedIn) {
    return <LoginContainer />;
  }

  // 현재 경로 → 활성 메뉴 + 헤더 제목 파생.
  // segments: ['jobs','5'] 같은 형태. 첫 세그먼트가 메뉴 id, /jobs/:id면 Job 상세.
  const segments = location.pathname.split('/').filter(Boolean);
  const activeId = segments[0] || 'dashboard';
  const headerTitle =
    activeId === 'jobs' && segments[1]
      ? `Job #${segments[1]}`
      : NAV_ITEMS.find((item) => item.id === activeId)?.label ?? '대시보드';

  // 로그인함 → 앱 렌더링
  return (
    <div css={shellStyle}>
      {/* 좌측 사이드 네비게이션 — id가 곧 경로(`/${id}`). */}
      <SideNav
        title="SAM Open Data"
        subtitle="수집 관리 콘솔"
        items={NAV_ITEMS}
        activeId={activeId}
        onSelect={(id) => navigate(`/${id}`)}
        // RUNNING Job이 있을 때만 "스케줄러 가동 중" 표시
        statusText={schedulerRunning ? '스케줄러 가동 중' : undefined}
        metaText="v1.4.9"
      />

      {/* 주요 콘텐츠 영역 */}
      <main css={mainStyle}>
        {/* 헤더: 제목 + 마지막 업데이트 + 사용자 정보 */}
        <AppHeader
          title={headerTitle}
          userName={user?.name ?? 'Admin'}
          onLogout={logout}
          // TODO: onRefresh — 현재 화면의 데이터 재조회 (useXxx.retry 호출)
        />

        {/* 화면 콘텐츠 — URL 라우팅 */}
        <div css={contentStyle}>
          <Routes>
            <Route path="/dashboard" element={<DashboardRoute />} />
            <Route path="/modules" element={<ModulesRoute />} />
            <Route path="/jobs" element={<JobsRoute />} />
            <Route path="/jobs/:id" element={<JobDetailRoute />} />
            <Route path="/data-browser" element={<DataBrowserContainer />} />
            <Route path="/population" element={<PopulationContainer />} />
            {/* 루트/미지정 경로 → 대시보드로 */}
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </div>
      </main>

      {/* 우상단 토스트 알림 */}
      <ToastContainer />
    </div>
  );
}

/**
 * AdminApp: Providers + Router 조립
 * - AuthProvider: 토큰 + 사용자 상태
 * - ToastProvider: 전역 토스트 알림
 * - BrowserRouter: URL 라우팅. basename=PUBLIC_URL 로 서브패스(/console 등) 흡수.
 * - AppContent: 실제 앱 콘텐츠
 */
export function AdminApp() {
  return (
    <AuthProvider>
      <ToastProvider>
        <BrowserRouter basename={process.env.PUBLIC_URL}>
          <AppContent />
        </BrowserRouter>
      </ToastProvider>
    </AuthProvider>
  );
}
